import { Children } from "react";

// Globale Variablen für den Zugriff auf AirTable
const AIRTABLE_API_KEY = process.env.REACT_APP_AIRTABLE_API_KEY;
const AIRTABLE_BASE_ID = process.env.REACT_APP_AIRTABLE_BASE_ID;

/**
 * Hilfsfunktion: Führt einen Fetch mit Wiederholungsversuchen bei API-Limit (HTTP 429) aus.
 * @param {string} url - Die URL für die Anfrage.
 * @param {object} options - Fetch-Optionen (Methode, Header, Body etc.).
 * @param {number} retries - Maximale Anzahl an Wiederholungsversuchen (Standard: 3).
 * @param {number} delayTime - Basis-Verzögerung in Millisekunden (Standard: 500).
 * @returns {Promise<Response>} - Die Antwort vom Fetch.
 */
const fetchWithRetry = async (url, options, retries = 3, delayTime = 500) => {
  for (let attempt = 0; attempt <= retries; attempt++) {
    const response = await fetch(url, options);
    if (response.status !== 429) {
      return response;
    }
    if (attempt < retries) {
      // Exponentielles Backoff: 500ms, 1000ms, 2000ms, …
      await new Promise((resolve) => setTimeout(resolve, delayTime * Math.pow(2, attempt)));
    } else {
      return response; // Letzter Versuch, auch wenn 429
    }
  }
};

/**
 * Sucht Datensätze in einer AirTable-Tabelle.
 *
 * @param {string} tableName - Name der Tabelle.
 * @param {object} [options] - Optionen für die Suche.
 * @param {number} [options.limit=100] - Maximale Anzahl an zurückgegebenen Datensätzen.
 * @param {string} [options.formula] - Optionale Formel für filterByFormula.
 * @returns {Promise<Array>} - Ein Array von Datensätzen oder ein leeres Array.
 *
 * Beispielaufruf:
 * searchRecordInAirTable('tasks', { limit: 150, formula: "FIND('important', {Priority})" })
 *   .then(records => console.log(records))
 *   .catch(err => console.error(err));
 *
 * Mögliche Rückgabe:
 * [
 *   { id: 'rec123', fields: { name: 'Task 1', Priority: 'high' }, ... },
 *   { id: 'rec124', fields: { name: 'Task 2', Priority: 'medium' }, ... },
 *   ...
 * ]
 */
export const searchRecordInAirTable = async (tableName, { limit = 100, formula } = {}) => {
  let records = [];
  let offset;
  const headers = {
    Authorization: `Bearer ${AIRTABLE_API_KEY}`,
    'Content-Type': 'application/json'
  };
  const userData = JSON.parse(localStorage.getItem('user'))
  console.log("UserDater: ",userData.workspace_id[0])

  do {
    // Standardseiten-Größe von 100 Datensätzen (Airtable Maximum)
    const params = new URLSearchParams();
    params.append('pageSize', '100');
    if (formula) {
      params.append('filterByFormula', 'AND({workspace_id}="'+userData.workspace_id[0]+'",'+formula+')');
    }else{
      params.append('filterByFormula', 'AND({workspace_id}="'+userData.workspace_id[0]+'")');
    }

    console.log("formula: ",params)

    if (offset) {
      params.append('offset', offset);
    }
    const url = `https://api.airtable.com/v0/${AIRTABLE_BASE_ID}/${tableName}?${params.toString()}`;

    const response = await fetchWithRetry(url, { headers });
    const data = await response.json();
    records = records.concat(data.records || []);
    offset = data.offset;
  } while (offset && records.length < limit);

  // Falls mehr Datensätze geladen wurden als benötigt, auf den gewünschten Limit zuschneiden.
  return records.slice(0, limit);
};

/**
 * Ruft einen einzelnen Datensatz anhand seiner ID ab.
 *
 * @param {string} recID - Die Record-ID.
 * @param {string} tableName - Name der Tabelle.
 * @returns {Promise<Object|null>} - Den Datensatz als Objekt oder null bei Fehlern.
 *
 * Beispielaufruf:
 * getRecordInAirTable('rec1234567890', 'tasks')
 *   .then(record => console.log(record))
 *   .catch(err => console.error(err));
 *
 * Mögliche Rückgabe:
 * { id: 'rec1234567890', fields: { name: 'Task 1', Priority: 'high' }, ... }
 */
export const getRecordInAirTable = async (recID, tableName) => {
  const headers = {
    Authorization: `Bearer ${AIRTABLE_API_KEY}`,
    'Content-Type': 'application/json'
  };

  try {
    const url = `https://api.airtable.com/v0/${AIRTABLE_BASE_ID}/${tableName}/${recID}`;
    const response = await fetchWithRetry(url, { method: 'GET', headers });
    const data = await response.json();
    if (response.ok) {
      console.log('Record fetched successfully:', data);
      return data;
    } else {
      console.error('Error fetching record:', data);
      return null;
    }
  } catch (err) {
    console.error('Network error:', err);
    return null;
  }
};

/**
 * Aktualisiert einen bestehenden Datensatz.
 *
 * @param {string} recID - Die Record-ID.
 * @param {object} recData - Die zu aktualisierenden Felder (im Format { Feldname: Wert }).
 * @param {string} tableName - Name der Tabelle.
 * @returns {Promise<Object|null>} - Das aktualisierte Datensatz-Objekt oder null bei Fehlern.
 *
 * Beispielaufruf:
 * const updatedData = { name: 'Updated Task', role: ['recRoleID12345'] };
 * updateRecordInAirTable('rec1234567890', updatedData, 'tasks')
 *   .then(updatedRecord => console.log(updatedRecord))
 *   .catch(err => console.error(err));
 *
 * Mögliche Rückgabe:
 * { id: 'rec1234567890', fields: { name: 'Updated Task', role: ['recRoleID12345'] }, ... }
 */
export const updateRecordInAirTable = async (recID, recData, tableName) => {
  const headers = {
    Authorization: `Bearer ${AIRTABLE_API_KEY}`,
    'Content-Type': 'application/json'
  };

  try {
    const url = `https://api.airtable.com/v0/${AIRTABLE_BASE_ID}/${tableName}/${recID}`;
    const response = await fetchWithRetry(url, {
      method: 'PATCH',
      headers,
      body: JSON.stringify({ fields: recData })
    });
    const data = await response.json();
    if (response.ok) {
      console.log('Record updated successfully:', data);
      return data;
    } else {
      console.error('Error updating record:', data);
      return null;
    }
  } catch (err) {
    console.error('Network error:', err);
    return null;
  }
};

/**
 * Erstellt einen neuen Datensatz.
 *
 * @param {object} recData - Die Felder des neuen Datensatzes (im Format { Feldname: Wert }).
 * @param {string} tableName - Name der Tabelle.
 * @returns {Promise<Object|null>} - Das erstellte Datensatz-Objekt oder null bei Fehlern.
 *
 * Beispielaufruf:
 * const newTask = { name: 'New Task', role: ['recRoleID12345'], clickup_tmpid: 'TMP006' };
 * createRecordInAirTable(newTask, 'tasks')
 *   .then(newRecord => console.log(newRecord))
 *   .catch(err => console.error(err));
 *
 * Mögliche Rückgabe:
 * { id: 'recNewlyCreated', fields: { name: 'New Task', role: ['recRoleID12345'], clickup_tmpid: 'TMP006' }, ... }
 */
export const createRecordInAirTable = async (recData, tableName) => {
  const headers = {
    Authorization: `Bearer ${AIRTABLE_API_KEY}`,
    'Content-Type': 'application/json'
  };

  const userData = JSON.parse(localStorage.getItem('user'))
  console.log("UserDater: ",userData.workspace_id[0])

  recData = {...recData, "workspace":[userData.workspace_id[0]]}

  try {
    const url = `https://api.airtable.com/v0/${AIRTABLE_BASE_ID}/${tableName}`;
    const response = await fetchWithRetry(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ fields: recData })
    });
    const data = await response.json();
    if (response.ok) {
      console.log('Record created successfully:', data);
      return data;
    } else {
      console.error('Error creating record:', data);
      return null;
    }
  } catch (err) {
    console.error('Network error:', err);
    return null;
  }
};



export const fetchFlows = async (setFlowVorlagen) => {
    const flows = await searchRecordInAirTable("flows",{formula:"{isTemplate}=TRUE()"});
    console.log("Fetched Flows:", flows);

    // Sortiere alphabetisch Gruppen nach groups[x].fields.f_name
    if (flows) {
      flows.sort((a, b) => a.fields.f_name.localeCompare(b.fields.f_name));
    }

    setFlowVorlagen(flows || []);
};

export const fetchGroups = async (setGruppenVorlagen) => {
    const groups = await searchRecordInAirTable("groups");
    console.log("Fetched Groups:", groups);
    setGruppenVorlagen(groups || []);
};

export const fetchTasks = async (setAufgabenVorlagen) => {
    const tasks = await searchRecordInAirTable("tasks");
    console.log("Fetched Tasks:", tasks);
    /*
    Return ein Array von Tasks
    (5) [{…}, {…}, {…}, {…}, {…}]
    0: 
    createdTime: "2025-02-07T18:15:37.000Z"
    fields: {name: 'Task A', role: Array(1), workspace: Array(1), clickup_tmpid: 'tmp_1c32b', f_name: 'Fliessbandbetrieb Task A', …}
    id: "recCPr1cBq98LKzLP"
    1: 
    {id: 'recLQblq9oXFqJ0S0', createdTime: '2025-02-07T18:15:37.000Z', fields: {…}}
    */
    setAufgabenVorlagen(tasks || []);
};

export const fetchRollen = async (setWorkspaceRoles) => {
    const roles = await searchRecordInAirTable("roles");
    console.log("Fetched Roles:", roles);
    setWorkspaceRoles(roles || []);
};

export const getTemplateDict = async (recId, tableName, saveResultDict) => {
    const dict = await getRecordInAirTable(recId, tableName);
    console.log("Fatched Template:", dict);
    saveResultDict(dict || null);
};











export const savingFunc = async (templteRecID, modus, isTemplateEditing, staticJSON) => {
  console.log(
    "Speichern:",
    modus,
    "templteRecID:",
    templteRecID,
    "isTemplateEditing:",
    isTemplateEditing,
    "staticJSON:",
    staticJSON
  );

  if (modus === "editGroup" && isTemplateEditing === true) {
    // Falls staticJSON als String übergeben wurde, parsen wir ihn
    let parsed;
    try {
      parsed = typeof staticJSON === "string" ? JSON.parse(staticJSON) : staticJSON;
    } catch (e) {
      console.error("Fehler beim Parsen der JSON:", e);
      return false;
    }

    // Annahme: Die Struktur der JSON sieht so aus:
    // {
    //   "id": "...",
    //   "name": "...",
    //   "lvl1_connections": [...],
    //   "blocks": [
    //     {
    //       "type": "group",
    //       // ... weitere statische Felder,
    //       "children_connections": [...],
    //       "children_blocks": [
    //         {
    //           "type": "task",
    //           "instance_id": "...",
    //           "template_id": "...",
    //           "instance_pos": { "x": ..., "y": ... },
    //           // weitere statische Felder, die wir nicht brauchen
    //         },
    //         ...
    //       ]
    //     }
    //   ]
    // }

    // Wir suchen den Gruppen-Block (hier nehmen wir an, dass es genau einen Block vom Typ "group" gibt)
    const groupBlock = parsed.blocks && parsed.blocks.find(block => block.type === "group");
    if (!groupBlock) {
      console.error("Kein Gruppen-Block gefunden.");
      return false;
    }

    // Extrahiere dynamisch die children_connections
    const children_connections = groupBlock.children_connections || [];

    // Definiere die Felder, die du aus jedem children_block behalten möchtest
    const allowedKeys = ["type", "instance_id", "template_id", "instance_pos"];
    const children = (groupBlock.children_blocks || []).map(block => {
      const newBlock = {};
      allowedKeys.forEach(key => {
        if (block.hasOwnProperty(key)) {
          newBlock[key] = block[key];
        }
      });
      return newBlock;
    });

    // Erstelle das dynamisch generierte Objekt
    const dataToSave = {
      children_connections,
      children
    };

    console.log("Dynamisch generierte JSON:", dataToSave);
    updateRecordInAirTable(
      templteRecID,
      { vorlageJSON: JSON.stringify(dataToSave) },
      "groups"
    );
  }
  else if (modus === "editFlow" && isTemplateEditing === true) {
    let parsed;
    try {
      parsed = typeof staticJSON === "string" ? JSON.parse(staticJSON) : staticJSON;
    } catch (e) {
      console.error("Fehler beim Parsen der JSON:", e);
      return false;
    }

    const allowedKeys = ["type", "instance_id", "template_id", "instance_pos"];
    const blocks = (parsed.blocks || []).map(block => {
      const newBlock = {};
      allowedKeys.forEach(key => {
        if (block.hasOwnProperty(key)) {
          newBlock[key] = block[key];
        }
      });
      return newBlock;
    });

    const dataToSave = {
      lvl1_connections: parsed.lvl1_connections || [],
      blocks
    };

    console.log("Dynamisch generierte JSON:", dataToSave);
    updateRecordInAirTable(
      templteRecID,
      { vorlageJSON: JSON.stringify(dataToSave) },
      "flows"
    );
  }

  alert("Speichern Erfolgreich")
  return true;
};
